import React, { useEffect, useState, useCallback } from "react";
import { clsx } from "clsx";
import { CheckCircle2, XCircle, AlertCircle, Info, X } from "lucide-react";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  duration?: number;
}

const icons: Record<
  ToastVariant,
  React.ComponentType<{ className?: string }>
> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const variantStyles: Record<ToastVariant, string> = {
  success:
    "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/30",
  error: "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30",
  warning:
    "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30",
  info: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30",
};

const iconStyles: Record<ToastVariant, string> = {
  success: "text-emerald-600 dark:text-emerald-400",
  error: "text-red-600 dark:text-red-400",
  warning: "text-amber-600 dark:text-amber-400",
  info: "text-blue-600 dark:text-blue-400",
};

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

function Toast({ toast, onDismiss }: ToastProps) {
  const Icon = icons[toast.variant];

  useEffect(() => {
    if (toast.duration === 0) return; // persistent
    // Errors and warnings stay until closed unless a caller sets a duration:
    // one that fades on its own can go unseen, and then nobody knows something
    // went wrong (Brennan 2026-10-15). Success and info fade after 5s.
    if (
      toast.duration === undefined &&
      (toast.variant === "error" || toast.variant === "warning")
    )
      return;
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration ?? 5000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, toast.variant, onDismiss]);

  return (
    <div
      className={clsx(
        "flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-slideDown",
        variantStyles[toast.variant],
      )}
    >
      <Icon
        className={clsx(
          "w-5 h-5 mt-0.5 flex-shrink-0",
          iconStyles[toast.variant],
        )}
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 dark:text-white">
          {toast.title}
        </p>
        {toast.message && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {toast.message}
          </p>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  position?: "top-right" | "bottom-right" | "top-center";
}

export function ToastContainer({
  toasts,
  onDismiss,
  position = "top-right",
}: ToastContainerProps) {
  const positionClasses = {
    "top-right": "top-4 right-4",
    "bottom-right": "bottom-4 right-4",
    "top-center": "top-4 left-1/2 -translate-x-1/2",
  };

  return (
    <div
      className={clsx(
        "fixed z-[100] flex flex-col gap-2 w-96 pointer-events-none",
        positionClasses[position],
      )}
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
}

/** Hook for managing toast state */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((toast: Omit<ToastItem, "id">) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, dismissToast };
}
